import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RecycleBinTab = ({ getAuthHeaders }) => {
  const [deletedItems, setDeletedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedItems, setSelectedItems] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchDeletedItems();
  }, [currentPage]);

  const fetchDeletedItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/recycle-bin?page=${currentPage}&limit=50`,
        getAuthHeaders()
      );
      setDeletedItems(response.data.items || []);
      setTotalPages(response.data.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch recycle bin items:', err);
      alert('Failed to load recycle bin items');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (item) => {
    if (!window.confirm(`Are you sure you want to restore this ${item._display_type}?`)) {
      return;
    }

    try {
      setActionLoading(true);
      await axios.post(
        `${BACKEND_URL}/api/admin/recycle-bin/restore`,
        {
          collection: item._collection,
          item_id: item.id
        },
        getAuthHeaders()
      );
      alert('Item restored successfully!');
      fetchDeletedItems();
    } catch (err) {
      console.error('Failed to restore item:', err);
      alert(err.response?.data?.detail || 'Failed to restore item');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePermanentDelete = async (item) => {
    if (!window.confirm(`⚠️ PERMANENT DELETE: This ${item._display_type} will be permanently deleted and cannot be recovered. Are you absolutely sure?`)) {
      return;
    }

    try {
      setActionLoading(true);
      await axios.delete(
        `${BACKEND_URL}/api/admin/recycle-bin/permanent`,
        {
          data: {
            collection: item._collection,
            item_id: item.id
          },
          ...getAuthHeaders()
        }
      );
      alert('Item permanently deleted');
      fetchDeletedItems();
    } catch (err) {
      console.error('Failed to permanently delete item:', err);
      alert(err.response?.data?.detail || 'Failed to permanently delete item');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleSelection = (itemId) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === deletedItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(deletedItems.map(item => item.id));
    }
  };

  const handleBulkRestore = async () => {
    if (selectedItems.length === 0) {
      alert('Please select items to restore');
      return;
    }

    if (!window.confirm(`Restore ${selectedItems.length} selected items?`)) {
      return;
    }

    try {
      setActionLoading(true);
      const itemsToRestore = deletedItems
        .filter(item => selectedItems.includes(item.id))
        .map(item => ({ collection: item._collection, item_id: item.id }));

      await axios.post(
        `${BACKEND_URL}/api/admin/recycle-bin/restore-bulk`,
        itemsToRestore,
        getAuthHeaders()
      );
      alert('Items restored successfully!');
      setSelectedItems([]);
      fetchDeletedItems();
    } catch (err) {
      console.error('Failed to bulk restore:', err);
      alert('Failed to restore items');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkPermanentDelete = async () => {
    if (selectedItems.length === 0) {
      alert('Please select items to delete');
      return;
    }

    if (!window.confirm(`⚠️ PERMANENT DELETE: ${selectedItems.length} items will be permanently deleted and cannot be recovered. Are you absolutely sure?`)) {
      return;
    }

    try {
      setActionLoading(true);
      const itemsToDelete = deletedItems
        .filter(item => selectedItems.includes(item.id))
        .map(item => ({ collection: item._collection, item_id: item.id }));

      await axios.delete(
        `${BACKEND_URL}/api/admin/recycle-bin/permanent-bulk`,
        {
          data: itemsToDelete,
          ...getAuthHeaders()
        }
      );
      alert('Items permanently deleted');
      setSelectedItems([]);
      fetchDeletedItems();
    } catch (err) {
      console.error('Failed to bulk delete:', err);
      alert('Failed to delete items');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getItemDisplayInfo = (item) => {
    // Extract relevant info based on item type
    const name = item.customer_name || item.name || item.full_name || item.email || 'Unknown';
    const info = item.email || item.pickup_location || item.message || '';
    return { name, info };
  };

  if (loading) {
    return <div className="p-4">Loading recycle bin...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            🗑️ Recycle Bin ({deletedItems.length} items)
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Items are automatically deleted after 30 days
          </p>
        </div>
        {selectedItems.length > 0 && (
          <div className="flex gap-2">
            <button
              onClick={handleBulkRestore}
              disabled={actionLoading}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
            >
              ↩️ Restore Selected ({selectedItems.length})
            </button>
            <button
              onClick={handleBulkPermanentDelete}
              disabled={actionLoading}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
            >
              🗑️ Permanently Delete Selected ({selectedItems.length})
            </button>
          </div>
        )}
      </div>

      {deletedItems.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <div className="text-6xl mb-4">✨</div>
          <p>Recycle bin is empty</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedItems.length === deletedItems.length && deletedItems.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded"
                    />
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name/Email</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deleted At</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {deletedItems.map((item) => {
                  const { name, info } = getItemDisplayInfo(item);
                  return (
                    <tr key={`${item._collection}-${item.id}`} className="hover:bg-gray-50">
                      <td className="px-3 py-4">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.id)}
                          onChange={() => toggleSelection(item.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-3 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {item._display_type}
                        </span>
                      </td>
                      <td className="px-3 py-4 text-sm">{name}</td>
                      <td className="px-3 py-4 text-sm text-gray-600 truncate max-w-xs">{info}</td>
                      <td className="px-3 py-4 text-sm text-gray-600">{formatDate(item.deleted_at)}</td>
                      <td className="px-3 py-4">
                        <button
                          onClick={() => handleRestore(item)}
                          disabled={actionLoading}
                          className="text-green-600 hover:text-green-900 mr-3 disabled:opacity-50"
                          title="Restore"
                        >
                          ↩️
                        </button>
                        <button
                          onClick={() => handlePermanentDelete(item)}
                          disabled={actionLoading}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          title="Permanently Delete"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex justify-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-4 py-2">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RecycleBinTab;
